/**
 * 워크스페이스 관리 API — 관리 콘솔 "워크스페이스 관리".
 *
 * admin BFF /api/admin/workspace/* → chat-server /api/v1/admin/workspace/* (catch-all proxy).
 * BFF 의 ALLOWED_PREFIXES 에 '/admin/' 이 이미 있어 BFF 코드 변경은 없다.
 *
 * 용량 단위는 전부 **바이트**다. 화면 입력은 GB 로 받고 여기서 환산한다.
 * limitBytes = -1 은 무제한을 뜻한다.
 */
import { apiClient } from './client';

export type QuotaScopeType = 'personal' | 'team' | 'company';
export type FileKind = 'all' | 'word' | 'excel' | 'ppt' | 'etc';

export interface WorkspaceOverview {
  totalBytes: number;
  fileCount: number;
  company: { limitBytes: number; unlimited: boolean; pct: number };
  byKind: Array<{ kind: string; bytes: number; count: number }>;
  topUploaders: Array<{ userId: string; name: string; email: string; bytes: number; count: number }>;
}

export interface UsageRow {
  scopeId: string;
  name: string;
  /** 개인=이메일 / 팀=본부명 */
  subtitle: string;
  usedBytes: number;
  limitBytes: number;
  fileCount: number;
  pct: number;
  unlimited: boolean;
  warn: boolean;
  /** 한도를 따로 지정하지 않아 기본값이 적용 중인가. */
  isDefault: boolean;
  // 개인 전용
  position?: string | null;
  status?: string;
  // 팀 전용
  memberCount?: number;
  isActive?: boolean;
}

export interface UsageResult {
  scope: 'personal' | 'team';
  defaultLimitBytes: number;
  rows: UsageRow[];
}

export interface AdminWorkspaceFile {
  id: string;
  name: string;
  docTitle: string | null;
  mimeType: string | null;
  kind: string;
  sizeBytes: number;
  ownerScope: string | null;
  ownerTeamId: string | null;
  verified: boolean;
  createdAt: string;
  uploader: { id: string; name: string; email: string };
}

export interface QuotaDefaults {
  personal: number;
  team: number;
  company: number;
  unlimitedSentinel: number;
}

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export async function getWorkspaceOverview(): Promise<WorkspaceOverview> {
  const { data } = await apiClient.get<WorkspaceOverview>('/admin/workspace/overview');
  return {
    ...data,
    byKind: asArray(data?.byKind),
    topUploaders: asArray(data?.topUploaders),
  };
}

export async function getWorkspaceUsage(scope: 'personal' | 'team'): Promise<UsageResult> {
  const { data } = await apiClient.get<UsageResult>('/admin/workspace/usage', { params: { scope } });
  return { ...data, rows: asArray<UsageRow>(data?.rows) };
}

export async function listWorkspaceFiles(params: {
  q?: string; kind?: FileKind; scope?: 'all' | QuotaScopeType; limit?: number; cursor?: string;
}): Promise<{ files: AdminWorkspaceFile[]; nextCursor: string | null }> {
  const clean: Record<string, string | number> = {};
  if (params.q) clean.q = params.q;
  if (params.kind && params.kind !== 'all') clean.kind = params.kind;
  if (params.scope && params.scope !== 'all') clean.scope = params.scope;
  if (params.limit) clean.limit = params.limit;
  if (params.cursor) clean.cursor = params.cursor;
  const { data } = await apiClient.get<{ files: AdminWorkspaceFile[]; nextCursor: string | null }>(
    '/admin/workspace/files', { params: clean },
  );
  return { files: asArray<AdminWorkspaceFile>(data?.files), nextCursor: data?.nextCursor ?? null };
}

export async function setWorkspaceQuota(body: {
  scopeType: QuotaScopeType;
  scopeId?: string;
  limitBytes: number;
}): Promise<UsageRow & { scopeType: QuotaScopeType }> {
  const { data } = await apiClient.patch('/admin/workspace/quota', body);
  return data;
}

export async function getQuotaDefaults(): Promise<QuotaDefaults> {
  const { data } = await apiClient.get<QuotaDefaults>('/admin/workspace/defaults');
  return data;
}

// ─── 용량 표기 헬퍼 ────────────────────────────────────────────────
const GB = 1024 * 1024 * 1024;

/** 바이트 → 사람이 읽는 표기. -1 은 호출부가 "무제한" 으로 분기한다. */
export function formatBytes(bytes: number): string {
  if (bytes < 0) return '무제한';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)}${units[i]}`;
}

export function bytesToGb(bytes: number): number {
  return Math.round((bytes / GB) * 100) / 100;
}

export function gbToBytes(gb: number): number {
  return Math.round(gb * GB);
}
